import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, Check, ExternalLink, Flame, Share2, Swords, X } from "lucide-react";
import { gameSubmit, referralId, withReferral } from "./engagement";
import { httpHref } from "./safeHref";
import { shareGameImage } from "./shareImage";
import { challengeText } from "./v3/gameShare";
import { scoreLocalGame } from "./v3/localGame";
import type { GamePick, GameResult, GameToday } from "./types";
import "./game.css";

/**
 * "Signal or Noise", InSnip's daily "can you tell what's real" game. A short set of claims,
 * some real (Signal), some fabricated (Noise). Swipe or tap each one, then a signature reveal
 * scores you, shows a receipt + source for every claim, and hands you a Wordle-style share.
 *
 * Two exports: SignalOrNoiseCard (the left-rail entry) and SignalOrNoiseGame (the full-screen
 * overlay). Both are self-contained: the only inputs are today's set, any stored result and a
 * couple of callbacks. Scoring is idempotent server-side, so a replay shows the same result.
 */

type Choice = "signal" | "noise";

/** Subtle haptic feedback (no-op where unsupported), mirrors the App shell's helper. */
function haptic(pattern: number | number[]) {
  if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
    try { navigator.vibrate(pattern); } catch { /* vibration not allowed */ }
  }
}

/** Readers who prefer reduced motion get an instant, flat reveal (no 3D flips). */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

// Wrong answer = a double buzz; a perfect round = a celebratory triple pulse.
const HAPTIC_WRONG: number[] = [30, 60, 30];
const HAPTIC_PERFECT: number[] = [40, 70, 40, 70, 40];

/** The share artifact: a Wordle-style text + emoji grid that carries the sharer's referral, so
 *  every share is a growth loop (the site already captures ?r=). */
function shareText(result: GameResult): string {
  return (
    `Signal or Noise · ${result.day}\n` +
    `${result.shareGrid}\n` +
    `${result.correct}/${result.total} · streak ${result.gameStreak} 🔥\n` +
    `Can you tell what's real? ${withReferral("https://insnip.com/")}`
  );
}

/** Share via the native sheet where available, else copy to clipboard. No canvas image (v1). */
async function shareResult(result: GameResult): Promise<"shared" | "copied" | "failed"> {
  const text = shareText(result);
  try {
    const nav = typeof navigator !== "undefined" ? navigator : undefined;
    if (nav?.share) {
      await nav.share({ title: "Signal or Noise", text });
      return "shared";
    }
    await nav?.clipboard?.writeText(text);
    return "copied";
  } catch {
    return "failed";
  }
}

/** The pip row: one pip per claim. Green/red once played (per item, or from the day summary),
 *  otherwise empty. Purely decorative, so it is hidden from assistive tech. */
function RailPips({ today, result }: { today: GameToday | null; result: GameResult | null }) {
  const count = today?.count ?? result?.total ?? 5;
  let states: ("correct" | "wrong" | "empty")[];
  if (result?.items?.length) {
    states = result.items.map((it) => (it.right ? "correct" : "wrong"));
  } else if (today?.play) {
    states = Array.from({ length: count }, (_, i) => (i < today.play!.correct ? "correct" : "wrong"));
  } else {
    states = Array.from({ length: count }, () => "empty");
  }
  return (
    <span className="son-pips" aria-hidden="true">
      {states.map((s, i) => (
        <span key={i} className={`son-pip ${s}`} />
      ))}
    </span>
  );
}

/**
 * The left-rail (and mobile-menu) entry card. Compact, styled like the Catch-up Brief widget.
 */
export function SignalOrNoiseCard({
  today,
  result,
  onPlay,
}: {
  today: GameToday | null;
  result: GameResult | null;
  onPlay: () => void;
}) {
  const [copied, setCopied] = useState(false);

  // No set for today yet: a calm, tasteful "being prepared" state.
  if (!today) {
    return (
      <section className="son-card son-card-prep">
        <span className="son-eyebrow">Signal or Noise · Today</span>
        <span className="son-tagline">Can you tell what's real?</span>
        <p className="son-prep">Today's round is being prepared. Check back soon.</p>
      </section>
    );
  }

  const played = Boolean(result) || Boolean(today.alreadyPlayed);
  const streak = result?.gameStreak ?? today.play?.gameStreak ?? 0;
  const score = result
    ? { c: result.correct, t: result.total }
    : today.play
      ? { c: today.play.correct, t: today.play.total }
      : null;

  async function onShare() {
    if (!result) {
      onPlay(); // no local grid to share yet: open the game, which fetches + offers share
      return;
    }
    // Image first: the branded score card drives far more click-throughs than the text grid.
    // Mirrors the reveal screen's share so every surface sends the same high-converting artifact.
    const img = await shareGameImage(result.day, result, result.gameStreak);
    if (img !== "failed") return;
    const r = await shareResult(result);
    if (r === "copied") {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    }
  }

  return (
    <section className={`son-card${played ? " played" : ""}`}>
      <button
        type="button"
        className="son-card-main"
        onClick={onPlay}
        aria-label={played ? "Review today's Signal or Noise result" : "Play today's Signal or Noise"}
      >
        <span className="son-eyebrow">Signal or Noise · Today</span>
        <span className="son-tagline">Can you tell what's real?</span>
        <RailPips today={today} result={result} />
        {streak > 0 ? (
          <span className="son-streak">
            <Flame size={13} aria-hidden="true" /> {streak} day streak
          </span>
        ) : null}
        {played && score ? (
          <span className="son-score">You scored {score.c}/{score.t} today</span>
        ) : (
          <span className="son-cta">
            Play today's round <ArrowRight size={13} aria-hidden="true" />
          </span>
        )}
      </button>
      {played ? (
        <div className="son-card-foot">
          <button type="button" className="son-share-mini" onClick={onShare}>
            {copied ? (
              <>
                <Check size={13} aria-hidden="true" /> Copied
              </>
            ) : (
              <>
                <Share2 size={13} aria-hidden="true" /> Share
              </>
            )}
          </button>
          <span className="son-back">Back tomorrow</span>
        </div>
      ) : null}
    </section>
  );
}

/** A single swipeable claim. Finger-follows via a lightweight pointer handler (no new deps);
 *  release past the threshold picks a side (right = Signal, left = Noise). Taps use the buttons. */
function SwipeCard({ claim, onPick }: { claim: string; onPick: (c: Choice) => void }) {
  const [dx, setDx] = useState(0);
  const startX = useRef<number | null>(null);
  const THRESH = 84;

  const onDown = (e: React.PointerEvent) => {
    startX.current = e.clientX;
    try {
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    } catch {
      /* pointer capture unsupported, dragging still works */
    }
  };
  const onMove = (e: React.PointerEvent) => {
    if (startX.current == null) return;
    setDx(e.clientX - startX.current);
  };
  const onUp = (e: React.PointerEvent) => {
    if (startX.current == null) return;
    const d = e.clientX - startX.current;
    startX.current = null;
    setDx(0);
    if (d > THRESH) onPick("signal");
    else if (d < -THRESH) onPick("noise");
  };

  const dir: Choice | null = dx > 12 ? "signal" : dx < -12 ? "noise" : null;
  const tagOpacity = Math.min(Math.abs(dx) / THRESH, 1);

  return (
    <div
      className={`son-item${dir ? ` lean-${dir}` : ""}`}
      style={{ transform: `translateX(${dx}px) rotate(${dx * 0.02}deg)` }}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      {dir ? (
        <span className={`son-swipe-tag ${dir}`} style={{ opacity: tagOpacity }} aria-hidden="true">
          {dir === "signal" ? "REAL" : "FAKE"}
        </span>
      ) : null}
      <span className="son-item-label">Real or fake?</span>
      <p className="son-claim">{claim}</p>
    </div>
  );
}

type Phase = "intro" | "playing" | "submitting" | "revealing" | "error" | "empty";

/**
 * The full-screen overlay: intro, one claim per screen, then the signature reveal.
 * Parent gates this with {open && <SignalOrNoiseGame open .../>}, so it mounts fresh each open.
 */
export function SignalOrNoiseGame({
  open,
  onClose,
  today,
  onComplete,
}: {
  open: boolean;
  onClose: () => void;
  today: GameToday | null;
  onComplete: (result: GameResult) => void;
}) {
  const ready = Boolean(today && today.count > 0);
  const [phase, setPhase] = useState<Phase>(() =>
    !ready ? "empty" : today!.alreadyPlayed ? "submitting" : "intro",
  );
  const [i, setI] = useState(0);
  const [picks, setPicks] = useState<GamePick[]>([]);
  const [result, setResult] = useState<GameResult | null>(null);
  const [shareMsg, setShareMsg] = useState<string | null>(null);
  const [challengeMsg, setChallengeMsg] = useState<string | null>(null);
  const [flipped, setFlipped] = useState<boolean[]>([]);
  const stageRef = useRef<HTMLDivElement | null>(null);

  const count = today?.count ?? 0;
  const perfect = result != null && result.total > 0 && result.correct === result.total;

  const loadReveal = useCallback(
    async (finalPicks: GamePick[]) => {
      if (!today) return;
      setPhase("submitting");
      // A local fallback round (no server set existed today) scores locally;
      // the reveal, receipts and share grid all behave identically.
      const r = today.mode === "local" ? scoreLocalGame(today.day, finalPicks) : await gameSubmit(today.day, finalPicks);
      if (r) {
        setResult(r);
        onComplete(r);
        setPhase("revealing");
      } else {
        setPhase("error");
      }
    },
    [today, onComplete],
  );

  // Replay: an already-played reader opens straight to a fresh (idempotent) reveal, no rescoring.
  useEffect(() => {
    if (today?.alreadyPlayed && today.count > 0) void loadReveal([]);
    // Runs once on mount (the component is remounted on every open).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = useCallback(
    (choice: Choice) => {
      if (!today) return;
      const item = today.items[i];
      if (!item) return;
      const next: GamePick[] = [...picks, { id: item.id, pick: choice }];
      setPicks(next);
      if (i + 1 < today.count) setI(i + 1);
      else void loadReveal(next);
    },
    [today, i, picks, loadReveal],
  );

  // Keyboard: Escape closes; while playing, Left/N = Noise, Right/S = Signal. The App shell's
  // global keydown early-returns while the game is open, so feed shortcuts do not leak through.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (phase !== "playing") return;
      const k = e.key.toLowerCase();
      if (e.key === "ArrowLeft" || k === "n") {
        e.preventDefault();
        pick("noise");
      } else if (e.key === "ArrowRight" || k === "s") {
        e.preventDefault();
        pick("signal");
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, phase, pick, onClose]);

  // The signature reveal: each item flips from "the claim as played" to its verdict,
  // staggered down the list. Wrong answers buzz as they flip; a perfect round earns a
  // celebratory pulse once the last card lands. Reduced motion: everything shows at once.
  useEffect(() => {
    if (phase !== "revealing" || !result) return;
    const n = result.items.length;
    const allRight = result.total > 0 && result.correct === result.total;
    if (prefersReducedMotion()) {
      setFlipped(Array.from({ length: n }, () => true));
      if (allRight) haptic(HAPTIC_PERFECT);
      else if (result.items.some((it) => !it.right)) haptic(HAPTIC_WRONG);
      return;
    }
    const timers = result.items.map((it, k) =>
      window.setTimeout(() => {
        setFlipped((prev) => {
          const next = prev.slice();
          next[k] = true;
          return next;
        });
        if (!it.right) haptic(HAPTIC_WRONG);
      }, 260 + k * 120),
    );
    if (allRight) {
      timers.push(window.setTimeout(() => haptic(HAPTIC_PERFECT), 260 + n * 120 + 400));
    }
    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [phase, result]);

  // Body-scroll lock + focus management while the overlay is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = window.setTimeout(() => stageRef.current?.focus(), 40);
    return () => {
      document.body.style.overflow = prev;
      window.clearTimeout(t);
    };
  }, [open]);

  async function onShareClick() {
    if (!result) return;
    // Image first: the branded score card sells the click; the text grid stays the fallback.
    const img = await shareGameImage(result.day, result, result.gameStreak);
    if (img !== "failed") {
      setShareMsg(img === "shared" ? "Shared" : "Saved");
      window.setTimeout(() => setShareMsg(null), 2200);
      return;
    }
    const r = await shareResult(result);
    if (r === "shared") setShareMsg("Shared");
    else if (r === "copied") setShareMsg("Copied");
    if (r !== "failed") window.setTimeout(() => setShareMsg(null), 2200);
  }

  // v3 Challenge a friend: a taunt plus a deep link (#game) that drops the recipient straight
  // into today's round. Text-first by design, a challenge is a message, not a poster.
  async function onChallengeClick() {
    if (!result) return;
    const origin = typeof window !== "undefined" ? window.location.origin : undefined;
    const text = challengeText(result, referralId() ?? "", origin);
    try {
      if (navigator.share) {
        await navigator.share({ title: "Signal or Noise", text });
        setChallengeMsg("Sent");
      } else {
        await navigator.clipboard?.writeText(text);
        setChallengeMsg("Copied");
      }
      window.setTimeout(() => setChallengeMsg(null), 2200);
    } catch {
      /* reader cancelled the share sheet */
    }
  }

  if (!open) return null;
  const item = today?.items[i];

  return (
    <section
      className="son-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Signal or Noise"
      onClick={onClose}
    >
      <div className="son-stage" ref={stageRef} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="son-close icon-button ghost"
          onClick={onClose}
          aria-label="Close Signal or Noise"
        >
          <X size={18} />
        </button>

        {phase === "empty" ? (
          <div className="son-empty">
            <span className="son-eyebrow">Signal or Noise</span>
            <h2 className="son-title">Today's round is being prepared</h2>
            <p className="son-sub">
              A fresh set of real and fake claims lands with the daily drop. Check back soon.
            </p>
            <button type="button" className="son-done" onClick={onClose}>Close</button>
          </div>
        ) : phase === "error" ? (
          <div className="son-empty">
            <span className="son-eyebrow">Signal or Noise</span>
            <h2 className="son-title">Could not score that round</h2>
            <p className="son-sub">Something went wrong reaching the scorer. Please try again.</p>
            <button type="button" className="son-play" onClick={() => void loadReveal(picks)}>
              Try again
            </button>
            <button type="button" className="son-done" onClick={onClose}>Close</button>
          </div>
        ) : phase === "submitting" ? (
          <div className="son-loading">
            <span className="son-spinner" aria-hidden="true" />
            <p>Scoring your round...</p>
          </div>
        ) : phase === "intro" ? (
          <div className="son-intro">
            <span className="son-eyebrow">Signal or Noise · {today?.day ?? "Today"}</span>
            <h2 className="son-title">Can you tell what's real?</h2>
            <p className="son-sub">
              {count} {count === 1 ? "claim" : "claims"}. Some are real, some are fabricated.
              Swipe or tap: real is Signal, fake is Noise.
            </p>
            <RailPips today={today} result={null} />
            <button type="button" className="son-play" onClick={() => setPhase("playing")}>
              Play <ArrowRight size={16} aria-hidden="true" />
            </button>
            <p className="son-hint">Right arrow or S for Signal. Left arrow or N for Noise.</p>
          </div>
        ) : phase === "playing" && item ? (
          <div className="son-play-wrap">
            <div className="son-progress" aria-hidden="true">
              {today!.items.map((_, k) => (
                <span key={k} className={`son-pip ${k < i ? "done" : k === i ? "active" : "empty"}`} />
              ))}
            </div>
            <span className="son-count">Item {i + 1} of {count}</span>
            <SwipeCard key={i} claim={item.claim} onPick={pick} />
            <div className="son-choices">
              <button type="button" className="son-choice noise" onClick={() => pick("noise")}>
                <strong>Noise</strong>
                <small>fake</small>
              </button>
              <button type="button" className="son-choice signal" onClick={() => pick("signal")}>
                <strong>Signal</strong>
                <small>real</small>
              </button>
            </div>
            <p className="son-hint">Swipe the claim, tap a choice, or use the arrow keys.</p>
          </div>
        ) : phase === "revealing" && result ? (
          <div className="son-reveal">
            <div className="son-result-head">
              <span className="son-eyebrow">Signal or Noise · {result.day}</span>
              <div className={`son-result-score${perfect ? " gshine" : ""}`}>
                <strong>{result.correct}</strong>
                <span>/ {result.total}</span>
              </div>
              {result.gameStreak > 0 ? (
                <span className="son-result-streak">
                  <Flame size={16} aria-hidden="true" /> {result.gameStreak} day streak
                </span>
              ) : null}
              {result.percentile > 0 ? (
                <p className="son-result-pct">
                  You did better than {result.percentile}% of players today.
                </p>
              ) : (
                <p className="son-result-pct">Your score is in. Come back tomorrow to beat it.</p>
              )}
            </div>

            <ol className="son-list">
              {result.items.map((it, k) => (
                <li key={it.id} className={`gflip${flipped[k] ? " flipped" : ""}`}>
                  <div className="gflip-inner">
                    {/* Front: the claim exactly as it was played. Decorative (the back
                        carries the full record), so it is hidden from assistive tech. */}
                    <div className="gflip-front son-reveal-item" aria-hidden="true">
                      <span className="son-item-label">Real or fake?</span>
                      <p className="son-reveal-claim">{it.claim}</p>
                      {it.yourPick ? (
                        <span className="g-your-pick">
                          You said {it.yourPick === "signal" ? "Signal · real" : "Noise · fake"}
                        </span>
                      ) : null}
                    </div>
                    <div className={`gflip-back son-reveal-item${it.right ? " right" : " wrong"}`}>
                      <div className="son-reveal-top">
                        <span className={`son-verdict${it.right ? " right" : " wrong"}`}>
                          {it.kind === "signal" ? "Signal · real" : "Noise · fake"}
                        </span>
                        <span className="son-mark" aria-hidden="true">
                          {it.right ? <Check size={14} /> : <X size={14} />}
                        </span>
                      </div>
                      <p className="son-reveal-claim">{it.claim}</p>
                      {it.receipt ? <p className="son-receipt">{it.receipt}</p> : null}
                      {(it.sourceName && it.sourceUrl) || (it.kind === "signal" && it.cardId) ? (
                        <div className="son-reveal-links">
                          {it.sourceName && httpHref(it.sourceUrl) ? (
                            <a
                              className="son-source"
                              href={httpHref(it.sourceUrl) ?? undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {it.sourceName} <ExternalLink size={12} aria-hidden="true" />
                            </a>
                          ) : null}
                          {it.kind === "signal" && it.cardId ? (
                            <a className="son-readmore" href={`/c/${it.cardId}`}>
                              Read the full snippet <ArrowRight size={12} aria-hidden="true" />
                            </a>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ol>

            <div className="son-reveal-actions">
              <button type="button" className="son-share" onClick={onShareClick}>
                {shareMsg ? (
                  <>
                    <Check size={15} aria-hidden="true" /> {shareMsg}
                  </>
                ) : (
                  <>
                    <Share2 size={15} aria-hidden="true" /> Share your result
                  </>
                )}
              </button>
              <button type="button" className="son-share son-challenge" onClick={() => void onChallengeClick()}>
                {challengeMsg ? (
                  <>
                    <Check size={15} aria-hidden="true" /> {challengeMsg}
                  </>
                ) : (
                  <>
                    <Swords size={15} aria-hidden="true" /> Challenge a friend
                  </>
                )}
              </button>
              <button type="button" className="son-done" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <div className="son-loading">
            <span className="son-spinner" aria-hidden="true" />
            <p>Loading today's round...</p>
          </div>
        )}
      </div>
    </section>
  );
}
